import CodeBlock from "@/components/docs/CodeBlock";
import MethodBadge from "@/components/docs/MethodBadge";
import ParamsTable from "@/components/docs/ParamsTable";
import { DocH3 } from "@/components/docs/Prose";
import { API, prettyJson, rateTier } from "@/lib/docs/api";
import type { ApiEndpoint } from "@/lib/docs/api";

const EndpointCard = ({ endpoint }: { endpoint: ApiEndpoint }) => {
  const tier = rateTier(endpoint.rateTier);
  const errors = [...endpoint.errors, ...API.sharedErrors].sort((a, b) => a.status - b.status);

  return (
    <article
      id={endpoint.id}
      className="flex scroll-mt-24 flex-col gap-6 border-t border-[#EFF2FF] pt-10 first:border-t-0 first:pt-0"
    >
      <header className="flex flex-col gap-3">
        <h2 className="text-[22px] font-semibold tracking-tight text-[#101828] sm:text-[24px]">
          <a href={`#${endpoint.id}`} className="hover:text-[#335CFF]">
            {endpoint.title}
          </a>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <MethodBadge method={endpoint.method} />
          <code className="break-all font-mono text-[13px] text-[#101828]">{endpoint.path}</code>
        </div>
        <p className="text-[15px] leading-[1.7] text-[#525866]">{endpoint.description}</p>
        {endpoint.notes.length > 0 && (
          <ul className="flex flex-col gap-1.5">
            {endpoint.notes.map((note) => (
              <li key={note} className="flex gap-3 text-[14px] leading-[1.65] text-[#525866]">
                <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-[#335CFF]" aria-hidden />
                {note}
              </li>
            ))}
          </ul>
        )}
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] text-[#525866]">
          <div className="flex gap-1.5">
            <dt className="font-medium text-[#101828]">Credits:</dt>
            <dd>{endpoint.credits}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="font-medium text-[#101828]">Rate limit:</dt>
            <dd>
              {tier.label} tier, {tier.perSecond}/s and {tier.perMinute}/min
            </dd>
          </div>
        </dl>
      </header>

      {endpoint.params.length > 0 && (
        <section className="flex flex-col gap-3">
          <DocH3>Parameters</DocH3>
          <ParamsTable params={endpoint.params} />
        </section>
      )}

      <section className="flex flex-col gap-3">
        <DocH3>Request</DocH3>
        <CodeBlock code={endpoint.curl} label="curl" />
      </section>

      <section className="flex flex-col gap-3">
        <DocH3>Response</DocH3>
        <CodeBlock code={prettyJson(endpoint.response)} label="200 application/json" />
      </section>

      <section className="flex flex-col gap-3">
        <DocH3>Errors</DocH3>
        <ul className="flex flex-col divide-y divide-[#EFF2FF] rounded-xl border border-[#EFF2FF]">
          {errors.map((error) => (
            <li
              key={`${error.status}-${error.code}`}
              className="flex flex-col gap-1 px-4 py-2.5 text-[13px] sm:flex-row sm:gap-4"
            >
              <span className="flex shrink-0 gap-2 font-mono text-[12px] sm:w-64">
                <span className="text-[#8B93A7]">{error.status}</span>
                <span className="text-[#101828]">{error.code}</span>
              </span>
              <span className="leading-[1.6] text-[#525866]">{error.when}</span>
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
};

export default EndpointCard;
